import React, { useEffect, useMemo, useRef } from 'react';
import { ActionButtonContainer, ActionButton, ActionButtonCluster } from 'styles/common.styles';
import { theme } from 'styles/theme';
import { isElementVisible } from 'utils/domVisibility';

/**
 * 액션 버튼 그룹 공통 컴포넌트
 *
 * - buttons: 기본 기능 (조회/추가/삭제/저장/엑셀) — 좌측
 * - extraButtons: 화면 전용 기능 (사진일괄, 승인 등) — 우측
 * - F7~F12: 보이는 탭의 버튼바만 처리 (비활성 탭 display:none 가로채기 방지)
 */
const ActionButtonGroup = ({
  buttons = [],
  extraButtons = [],
  loading = false,
  showLoadingText = true,
}) => {
  const shellRef = useRef(null);

  const allButtons = useMemo(
    () => [...(buttons || []), ...(extraButtons || [])],
    [buttons, extraButtons]
  );

  const hotkeyMap = useMemo(() => {
    const map = new Map();
    const normalizeHotkey = (v) => String(v || '').trim().toUpperCase();
    for (const btn of allButtons) {
      const key = normalizeHotkey(btn?.hotkey);
      if (!key) continue;
      const isDisabled = loading || btn?.disabled === true;
      if (isDisabled) continue;
      if (typeof btn?.onClick !== 'function') continue;
      map.set(key, btn.onClick);
    }
    return map;
  }, [allButtons, loading]);

  useEffect(() => {
    if (!hotkeyMap.size) return undefined;

    const isTypingTarget = (el) => {
      if (!el) return false;
      const tag = (el.tagName || '').toUpperCase();
      return (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        el.isContentEditable === true
      );
    };

    const onKeyDown = (e) => {
      // 숨은 탭(마운트 유지)의 F키 리스너가 활성 화면을 가로채지 않음
      if (!isElementVisible(shellRef.current)) return;

      // ERP 단축키(F7~F12 등)는 셀 편집 중에도 동작해야 함
      const key = String(e.key || '').toUpperCase();
      const isFunctionKey = /^F([1-9]|1[0-2])$/.test(key);
      if (!isFunctionKey && isTypingTarget(e.target)) return;
      if (e.repeat) return;

      const handler = hotkeyMap.get(key);
      if (!handler) return;

      e.preventDefault();
      e.stopPropagation();
      handler(e);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [hotkeyMap]);

  const renderButton = (button) => {
    const {
      id,
      label,
      icon,
      onClick,
      variant,
      disabled = false,
      hotkey,
      ...buttonProps
    } = button;

    return (
      <ActionButton
        key={id}
        onClick={onClick}
        disabled={loading || disabled}
        $variant={variant}
        title={hotkey ? `${label} (${hotkey})` : label}
        {...buttonProps}
      >
        {icon && <span>{icon}</span>}
        {label}
      </ActionButton>
    );
  };

  return (
    <ActionButtonContainer ref={shellRef} $hasExtra={!!extraButtons?.length}>
      <ActionButtonCluster>
        {buttons.map(renderButton)}
        {showLoadingText && loading && (
          <span style={{ marginLeft: '4px', color: theme.colors.text.secondary, alignSelf: 'center' }}>처리 중...</span>
        )}
      </ActionButtonCluster>
      {extraButtons?.length > 0 && (
        <ActionButtonCluster>
          {extraButtons.map(renderButton)}
        </ActionButtonCluster>
      )}
    </ActionButtonContainer>
  );
};

export default ActionButtonGroup;
